import { useEffect, useState } from 'react';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import type { ISupervisionControlData } from '../../TOC/v1/supervisionControlData';
import { CompletenessOptions } from '../../../../../../common/enums/completenessOptions';
import { SupervisorsAssessmentBiometrics } from '../../../../../../common/enums/supervisorsAssessmentBiometrics';
import { translateKey } from '../../../../../translate';
import { FormDivider, HeaderDivider, InputCheckbox, InputRadioGroup, InputTextArea } from '../../../../elements/common';
import { FormLabel, HorizontalFormGroup, InputContainer } from '../../../../elements/forms';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { initialSupervisionData } from '../../TOC/v1/types';

export function TooV1SupervisionAddEdit(props: ICustomControlDiagnosticReportAddEditProps): JSX.Element {
    const { diagnosticReport, errors, handleChangeCustomControl, process, validateOneCustomControl } = props;

    const [isFeedbackCheckboxChecked, setIsFeedbackCheckboxChecked] = useState(
        diagnosticReport.customControlData.hasSupervisionFields
    );
    const [supervisionControlData, setSupervisionControlData] = useState<ISupervisionControlData>(
        diagnosticReport.customControlData.supervision || { ...initialSupervisionData }
    );

    const reviewOptions = [
        SupervisorsAssessmentBiometrics.good,
        SupervisorsAssessmentBiometrics.moderate,
        SupervisorsAssessmentBiometrics.poor,
    ];

    const reviewOptionsLabels = [
        translateKey('supervisorsAssessmentBiometricsEnum.good'),
        translateKey('supervisorsAssessmentBiometricsEnum.moderate'),
        translateKey('supervisorsAssessmentBiometricsEnum.poor'),
    ];

    const completenessOptions = [
        CompletenessOptions.goed,
        CompletenessOptions.voldoende,
        CompletenessOptions.onvoldoende,
    ];

    useEffect(() => {
        handleChangeCustomControl({
            supervision: supervisionControlData,
        });
    }, [supervisionControlData]);

    useEffect(() => {
        handleChangeCustomControl({ hasSupervisionFields: isFeedbackCheckboxChecked });

        if (!isFeedbackCheckboxChecked || !diagnosticReport.customControlData.supervision) {
            setSupervisionControlData({ ...initialSupervisionData });
        }
    }, [isFeedbackCheckboxChecked]);

    return (
        <>
            <HorizontalFormGroup>
                <FormLabel>
                    <InputCheckbox
                        checked={isFeedbackCheckboxChecked}
                        onChange={(value) => {
                            setIsFeedbackCheckboxChecked(value);
                        }}
                        text={translateKey('tooSupervision.feedbackTitle')}
                    />
                </FormLabel>
            </HorizontalFormGroup>
            {isFeedbackCheckboxChecked && (
                <>
                    <FormDivider />
                    <ProcessItemHeader
                        isFirstItem={false}
                        isItemOpen={true}
                        process={process}
                        title={translateKey('tooSupervision.feedbackTitle')}
                    />
                    <HeaderDivider text={translateKey('activityTypeEnum.biometrics')} />
                    {feedbackRadioGroup('assessmentBiometrics', reviewOptions, reviewOptionsLabels)}
                    {feedbackRadioGroup('assessmentCompleteness', completenessOptions, completenessOptions)}
                    {remarksTextGroup('remarkBiometrics')}
                    <HeaderDivider text={translateKey('diagnosticReport.findings')} />
                    {feedbackRadioGroup('assessmentQuestion', reviewOptions, reviewOptionsLabels)}
                    {feedbackRadioGroup('assessmentDiagnosis', reviewOptions, reviewOptionsLabels)}
                    {remarksTextGroup('remarkGrading')}
                </>
            )}
        </>
    );

    function fieldOnChange(key: keyof ISupervisionControlData, value?: string | null) {
        setSupervisionControlData({ ...supervisionControlData, [key]: value });
    }

    function remarksTextGroup(key: keyof ISupervisionControlData) {
        return (
            <>
                <HorizontalFormGroup hasErrors={errors.has(`customControlData.supervision.${key}`)}>
                    <FormLabel isRequired={false}>{translateKey(`tooSupervision.${key}`)}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation={key}
                            error={errors.get(`customControlData.supervision.${key}`)}
                            onChange={(value) => fieldOnChange(key, value)}
                            onDoValidate={async () => {
                                validateOneCustomControl(`supervision.${key}`);
                            }}
                            placeholder="Een toelichting, opmerkingen of vragen"
                            value={supervisionControlData[key]}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    function feedbackRadioGroup(key: keyof ISupervisionControlData, options: string[], labels: string[]) {
        return (
            <>
                <HorizontalFormGroup hasErrors={errors.has(`customControlData.supervision.${key}`)}>
                    <FormLabel isRequired>{translateKey(`tooSupervision.${key}`)}</FormLabel>
                    <InputRadioGroup
                        automation={key}
                        error={errors.get(`customControlData.supervision.${key}`)}
                        labels={labels}
                        name={key}
                        onChange={(value) => fieldOnChange(key, value)}
                        onDoValidate={async () => {
                            validateOneCustomControl(`supervision.${key}`);
                        }}
                        options={options}
                        value={supervisionControlData[key]}
                    />
                </HorizontalFormGroup>
            </>
        );
    }
}
